import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, AxiosResponse } from 'axios';
import { API_BASE_URL } from '@/config/api';
import { authEvents, AuthEvent } from '@/lib/auth/authEvents';
import { apiPerformanceMonitor } from '@/lib/utils/performanceMonitor';

export class ApiClient {
  private client: AxiosInstance;
  // Note: No token storage needed - using secure HttpOnly cookies

  constructor() {
    // Use API_BASE_URL directly as it already includes the proper base path
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      withCredentials: true // Enable credentials for secure cookie authentication
    });

    // Add request interceptor for logging and performance monitoring
    // Note: No manual token handling needed - cookies are sent automatically
    this.client.interceptors.request.use(
      (config) => {
        // Cookies are automatically included via withCredentials: true
        // No manual Authorization header needed

        // Start performance timing
        const finishTiming = apiPerformanceMonitor.startTiming(
          config.url || 'unknown',
          config.method?.toUpperCase() || 'GET'
        );
        
        // Store timing function in config for response interceptor
        (config as any)._finishTiming = finishTiming;

        // Log minimal request info in development
        if (import.meta.env.DEV) {
          console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
        }
        return config;
      },
      (error) => {
        if (import.meta.env.DEV) {
          console.error('[API] Request error:', error);
        }
        return Promise.reject(error);
      }
    );

    // Add response interceptor for logging and error handling
    this.client.interceptors.response.use(
      (response) => {
        // Finish performance timing
        const finishTiming = (response.config as any)?._finishTiming;
        if (finishTiming) {
          finishTiming(response.status, false);
        }

        // Log minimal response info in development
        if (import.meta.env.DEV) {
          const isSuccessful = response.data?.success !== false;
          console.log(`[API] Response: ${response.status} ${isSuccessful ? 'OK' : 'Failed'}`);
        }
        
        return response;
      },
      async (error: AxiosError) => {
        // Finish performance timing for errors
        const finishTiming = (error.config as any)?._finishTiming;
        if (finishTiming) {
          finishTiming(error.response?.status, true);
        }

        if (import.meta.env.DEV) {
          console.error('[API] Response error:', {
            url: error.config?.url,
            status: error.response?.status,
            message: error.message
          });
        }

        // Handle specific error cases
        if (error.code === 'ERR_NETWORK') {
          return Promise.reject({
            success: false,
            error: 'Network error: Unable to connect to the API. Please check your network connection and try again.',
            status: 0
          });
        }

        if (error.response?.status === 401) {
          // Don't redirect if we're already trying to authenticate or refresh
          if (!error.config?.url?.includes('/auth/')) {
            // Try to refresh token first before redirecting to auth
            try {
              const refreshResponse = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json',
                }
              });

              if (refreshResponse.ok) {
                // Token refreshed successfully, retry the original request
                if (import.meta.env.DEV) {
                  console.log('[API] Token refreshed, retrying request');
                }
                return this.client.request(error.config!);
              } else {
                // Refresh failed - emit session expired event with detailed diagnostics
                const errorDetails = {
                  reason: 'Token refresh failed',
                  status: refreshResponse.status,
                  statusText: refreshResponse.statusText,
                  timestamp: new Date().toISOString()
                };
                
                if (import.meta.env.DEV) {
                  console.log('[API] Token refresh failed, emitting session expired event:', errorDetails);
                }
                
                authEvents.emit(AuthEvent.SESSION_EXPIRED, errorDetails);
              }
            } catch (refreshError) {
              // Refresh failed - emit session expired event with detailed diagnostics
              const errorDetails = {
                reason: 'Token refresh error',
                error: refreshError,
                message: refreshError instanceof Error ? refreshError.message : 'Unknown error',
                timestamp: new Date().toISOString()
              };
              
              if (import.meta.env.DEV) {
                console.error('[API] Token refresh failed:', errorDetails);
              }
              
              authEvents.emit(AuthEvent.SESSION_EXPIRED, errorDetails);
            }
          } else {
            // Already on auth endpoint, emit session invalid with diagnostics
            const errorDetails = {
              reason: 'Authentication endpoint returned 401',
              url: error.config?.url,
              method: error.config?.method,
              timestamp: new Date().toISOString()
            };
            
            if (import.meta.env.DEV) {
              console.log('[API] Auth endpoint returned 401, emitting session invalid event:', errorDetails);
            }
            
            authEvents.emit(AuthEvent.SESSION_INVALID, errorDetails);
          }
          // Fail gracefully without forcing redirects
          return Promise.reject({
            success: false,
            error: 'Authentication required. Please log in again.',
            status: 401
          });
        }

        if (error.response?.status === 403) {
          return Promise.reject({
            success: false,
            error: 'Access denied. You do not have permission to perform this action.',
            status: 403
          });
        }

        return Promise.reject(error);
      }
    );
  }

  // Note: Token management methods removed - using secure HttpOnly cookies
  // Authentication is handled automatically via browser cookie transmission

  /**
   * Perform a GET request to retrieve data from the database
   * @param url The API endpoint to fetch data from
   * @param config Optional request configuration
   * @returns A normalized API response containing the requested data
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.get<ApiResponse<T>>(url, config);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return {
          success: false,
          error: error.response?.data?.message || error.message,
          status: error.response?.status
        };
      }
      return {
        success: false,
        error: 'An unexpected error occurred',
        status: 500
      };
    }
  }

  /**
   * Perform a POST request to create a new resource in the database
   * @param url The API endpoint to send data to
   * @param data The data to be created
   * @param config Optional request configuration
   * @returns A normalized API response containing the created data
   */
  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.post<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return {
          success: false,
          error: error.response?.data?.message || error.message,
          status: error.response?.status
        };
      }
      return {
        success: false,
        error: 'An unexpected error occurred',
        status: 500
      };
    }
  }

  /**
   * Perform a PUT request to update an existing resource in the database
   * @param url The API endpoint for the resource to update
   * @param data The data to be updated
   * @param config Optional request configuration
   * @returns A normalized API response containing the updated data
   */
  async put<T>(url: string, data: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.put<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return {
          success: false,
          error: error.response?.data?.message || error.message,
          status: error.response?.status
        };
      }
      return {
        success: false,
        error: 'An unexpected error occurred',
        status: 500
      };
    }
  }

  /**
   * Perform a PATCH request to update an existing resource in the database
   * @param url The API endpoint for the resource to update
   * @param data The data to be updated
   * @param config Optional request configuration
   * @returns A normalized API response containing the updated data
   */
  async patch<T>(url: string, data: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.patch<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return {
          success: false,
          error: error.response?.data?.message || error.message,
          status: error.response?.status
        };
      }
      return {
        success: false,
        error: 'An unexpected error occurred',
        status: 500
      };
    }
  }

  /**
   * Perform a DELETE request to remove a resource from the database
   * @param url The API endpoint for the resource to delete
   * @param config Optional request configuration
   * @returns A normalized API response indicating deletion status
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.delete<ApiResponse<T>>(url, config);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        return {
          success: false,
          error: error.response?.data?.message || error.message,
          status: error.response?.status
        };
      }
      return {
        success: false,
        error: 'An unexpected error occurred',
        status: 500
      };
    }
  }
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  status?: number;
}

export const apiClient = new ApiClient();
